import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import apiInstance from '../../utils/axios';
import GetCurrentAddress from '../plugin/UserCountry';
import UserData from '../plugin/UserData';
import getCartID from '../plugin/cartID';
import Swal from 'sweetalert2';
import { CartContext } from '../../App';
import PriceFormat_Basic from '../../components/commerce-ui/price-format-basic';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  Search as SearchIcon, 
  Package, 
  SlidersHorizontal, 
  X, 
  ChevronDown, 
  ChevronUp,
  Star,
  Truck,
  Check,
  Heart,
  Eye,
  ShoppingCart,
  Sparkles,
  Filter,
  Grid3X3,
  LayoutGrid,
  Tag
} from 'lucide-react';

// Fuzzy Search Utilities
const levenshteinDistance = (str1, str2) => {
  const m = str1.length;
  const n = str2.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
  }
  return dp[m][n];
};

const fuzzyMatch = (text, query) => {
  if (!text || !query) return { match: false, score: 0 };
  
  const textLower = text.toLowerCase();
  const queryLower = query.toLowerCase();
  
  // Exact match - highest score
  if (textLower.includes(queryLower)) {
    return { match: true, score: 100 };
  }
  
  // Check if query words match in any order
  const queryWords = queryLower.split(/\s+/).filter(w => w.length > 0);
  const textWords = textLower.split(/\s+/);
  
  let matchedWords = 0;
  let fuzzyScore = 0;
  
  for (const queryWord of queryWords) {
    if (queryWord.length < 2) continue;
    
    let bestWordScore = 0;
    
    for (const textWord of textWords) {
      if (textWord.includes(queryWord) || queryWord.includes(textWord)) {
        bestWordScore = Math.max(bestWordScore, 90);
        continue;
      }
      
      if (queryWord.length >= 3) {
        const distance = levenshteinDistance(queryWord, textWord);
        const maxLen = Math.max(queryWord.length, textWord.length);
        const similarity = ((maxLen - distance) / maxLen) * 100;
        const tolerance = queryWord.length <= 5 ? 2 : 3;
        if (distance <= tolerance && similarity > 50) {
          bestWordScore = Math.max(bestWordScore, similarity);
        }
      }
      
      if (queryWord[0] === textWord[0] && 
          Math.abs(queryWord.length - textWord.length) <= 2 &&
          queryWord.length >= 3) {
        const distance = levenshteinDistance(queryWord, textWord);
        if (distance <= 3) {
          const similarity = ((Math.max(queryWord.length, textWord.length) - distance) / Math.max(queryWord.length, textWord.length)) * 85;
          bestWordScore = Math.max(bestWordScore, similarity);
        }
      }
    }
    
    if (bestWordScore > 50) {
      matchedWords++;
      fuzzyScore += bestWordScore;
    }
  }
  
  if (matchedWords > 0) {
    const avgScore = fuzzyScore / queryWords.length;
    const coverageBonus = (matchedWords / queryWords.length) * 20;
    return { match: true, score: avgScore + coverageBonus };
  }
  
  if (queryLower.length >= 3) {
    for (const textWord of textWords) {
      if (textWord.length >= 3) {
        const distance = levenshteinDistance(queryLower, textWord);
        const maxLen = Math.max(queryLower.length, textWord.length);
        const similarity = ((maxLen - distance) / maxLen) * 100;
        if (distance <= 2 && similarity > 60) {
          return { match: true, score: similarity };
        }
      }
    }
  }
  
  return { match: false, score: 0 };
};

const Toast = Swal.mixin({
  toast: true,
  position: 'top',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
});

function Search() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('query');

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    categories: [],
    vendors: [],
    priceRange: { min: 0, max: 10000 },
    inStock: false,
    freeShipping: false,
    onSale: false,
    rating: 0,
  });
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('relevant');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    categories: true,
    price: true,
    availability: true,
    vendors: false,
  });
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });

  const [cartCount, setCartCount] = useContext(CartContext);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle responsive breakpoints
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const currentAddress = GetCurrentAddress();
  const userData = UserData();
  const cart_id = getCartID();

  // Fetch products based on search query
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch categories for filters
        const categoriesRes = await apiInstance.get('category/');
        setCategories(categoriesRes.data);
        
        // Fetch products based on search query
        if (query) {
          const productsRes = await apiInstance.get(`search/?query=${encodeURIComponent(query)}`);
          let searchResults = Array.isArray(productsRes.data) ? productsRes.data : (productsRes.data.results || []);
          
          // If backend returned few results, also fetch all products for client-side fuzzy matching
          if (searchResults.length < 5) {
            try {
              const allProductsRes = await apiInstance.get('products/');
              const allProducts = Array.isArray(allProductsRes.data) ? allProductsRes.data : (allProductsRes.data.results || []);
              
              // Fuzzy match against all products
              const existingIds = new Set(searchResults.map(p => p.id || p.pid));
              const fuzzyMatches = allProducts.filter(product => {
                if (existingIds.has(product.id || product.pid)) return false;
                const title = product.title_translated || product.title || '';
                const desc = product.description_translated || product.description || '';
                const category = product.category?.title_translated || product.category?.title || '';
                
                return fuzzyMatch(title, query).match || 
                       fuzzyMatch(desc, query).match || 
                       fuzzyMatch(category, query).match;
              });
              
              searchResults = [...searchResults, ...fuzzyMatches];
            } catch (e) {
              console.log('Fuzzy fallback failed:', e);
            }
          }
          
          setProducts(searchResults);
        } else {
          const productsRes = await apiInstance.get('products/');
          setProducts(productsRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        Toast.fire({
          icon: 'error',
          title: t('errorSearching'),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [query]);

  // Apply filters and sorting
  useEffect(() => {
    if (!loading && products.length > 0) {
      let results = [...products];

      // Apply fuzzy search scoring for better relevance when there's a query
      if (query && query.trim()) {
        const scoredResults = results.map(product => {
          const title = product.title_translated || product.title || '';
          const desc = product.description_translated || product.description || '';
          const category = product.category?.title_translated || product.category?.title || '';
          
          const titleMatch = fuzzyMatch(title, query);
          const descMatch = fuzzyMatch(desc, query);
          const categoryMatch = fuzzyMatch(category, query);
          
          const bestScore = Math.max(
            titleMatch.score * 1.2,
            categoryMatch.score * 1.1,
            descMatch.score
          );
          
          const isMatch = titleMatch.match || descMatch.match || categoryMatch.match;
          
          return { product, score: bestScore, isMatch };
        });
        
        // Only keep products that have some match (fuzzy or backend returned)
        results = scoredResults
          .filter(item => item.isMatch || item.score > 0)
          .sort((a, b) => b.score - a.score)
          .map(item => item.product);
          
        // If fuzzy matching filtered out everything, show all backend results
        if (results.length === 0) {
          results = [...products];
        }
      }

      // Apply category filters
      if (activeFilters.categories.length > 0) {
        results = results.filter(product => 
          activeFilters.categories.includes(product.category?.title)
        );
      }

      // Apply vendor filters
      if (activeFilters.vendors.length > 0) {
        results = results.filter(product => 
          activeFilters.vendors.includes(product.vendor?.name)
        );
      }

      // Apply price range filter
      results = results.filter(product => {
        const price = parseFloat(product.price);
        return price >= activeFilters.priceRange.min && price <= activeFilters.priceRange.max;
      });

      // Apply in stock filter
      if (activeFilters.inStock) {
        results = results.filter(product => {
          const stock = product.stock_qty || product.stock || product.quantity || 0;
          return stock > 0;
        });
      }

      // Apply free shipping filter
      if (activeFilters.freeShipping) {
        results = results.filter(product => product.shipping_amount === 0);
      }

      // Apply on sale filter
      if (activeFilters.onSale) {
        results = results.filter(product => product.old_price && product.old_price > product.price);
      }

      // Apply sorting
      switch (sortBy) {
        case 'price-low':
          results.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
          break;
        case 'price-high':
          results.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
          break;
        case 'newest':
          results.sort((a, b) => new Date(b.date) - new Date(a.date));
          break;
        case 'discount':
          results.sort((a, b) => {
            const discountA = a.old_price ? ((a.old_price - a.price) / a.old_price) * 100 : 0;
            const discountB = b.old_price ? ((b.old_price - b.price) / b.old_price) * 100 : 0;
            return discountB - discountA;
          });
          break;
        default:
          break;
      }

      setFilteredProducts(results);
    }
  }, [products, loading, activeFilters, sortBy, query]);

  const handleFilterChange = (filterType, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...prev[filterType], value]
    }));
  };

  const handlePriceRangeChange = (min, max) => {
    setActiveFilters(prev => ({
      ...prev,
      priceRange: { min, max }
    }));
  };

  const handleToggleFilter = (filterName, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    if (category) {
      setActiveFilters(prev => ({
        ...prev,
        categories: [category.title]
      }));
    } else {
      setActiveFilters(prev => ({
        ...prev,
        categories: []
      }));
    }
  };

  const clearAllFilters = () => {
    setActiveFilters({
      categories: [],
      vendors: [],
      priceRange: { min: 0, max: 10000 },
      inStock: false,
      freeShipping: false,
      onSale: false,
      rating: 0,
    });
    setSelectedCategory(null);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (activeFilters.categories.length > 0) count += activeFilters.categories.length;
    if (activeFilters.vendors.length > 0) count += activeFilters.vendors.length;
    if (activeFilters.priceRange.min > 0 || activeFilters.priceRange.max < 10000) count += 1;
    if (activeFilters.inStock) count += 1;
    if (activeFilters.freeShipping) count += 1;
    if (activeFilters.onSale) count += 1;
    return count;
  };

  // Add to cart function
  const addToCart = async (productId, price, shipping_ammount) => {
    const formdata = new FormData();
    formdata.append('product_id', productId);
    formdata.append('user_id', userData?.user_id || 0);
    formdata.append('qty', 1);
    formdata.append('price', price);
    formdata.append('shipping_ammount', shipping_ammount);
    formdata.append('country', currentAddress.country);
    formdata.append('size', 'No Size');
    formdata.append('color', 'No Color');
    formdata.append('cart_id', cart_id);

    try {
      const response = await apiInstance.post('cart-view/', formdata);
      Toast.fire({
        icon: 'success',
        title: response.data.message,
      });
      
      const cartResponse = await apiInstance.get(`cart-list/${cart_id}/`);
      setCartCount(cartResponse.data.length);
    } catch (error) {
      console.error('Error adding to cart:', error);
      let errorMessage = t('errorAddingToCart');
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      Toast.fire({
        icon: 'error',
        title: errorMessage,
      });
    }
  };

  const addToWishlist = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();

    if (!userData?.user_id) {
      Toast.fire({
        icon: 'warning',
        title: t('loginRequired') || 'Inicia sesión para agregar a favoritos',
      });
      return;
    }

    try {
      const response = await apiInstance.post('add-to-wishlist/', {
        product_id: product.id,
        user_id: userData.user_id,
      });
      Toast.fire({
        icon: 'success',
        title: t('addedToWishlist') || 'Agregado a favoritos ❤️',
      });
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      if (error.response?.status === 400 || error.response?.data?.error?.includes('already')) {
        Toast.fire({
          icon: 'info',
          title: t('alreadyInWishlist') || 'Ya está en tu lista de favoritos',
        });
      } else {
        Toast.fire({
          icon: 'error',
          title: t('errorAddingToWishlist') || 'Error al agregar a favoritos',
        });
      }
    }
  };

  const handleImageMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomOrigin({ x, y });
  };

  const handleImageMouseLeave = () => {
    setZoomOrigin({ x: 50, y: 50 });
  };

  const uniqueVendors = [...new Set(products.map(p => p.vendor?.name))].filter(Boolean);

  // Filter Section Component
  const FilterSection = ({ title, icon: Icon, expanded, onToggle, children }) => (
    <div className="filter-section">
      <div className="filter-section-header" onClick={onToggle}>
        <div className="filter-section-title">
          <Icon size={18} />
          <span>{title}</span>
        </div>
        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </div>
      {expanded && <div className="filter-section-content">{children}</div>}
    </div>
  );

  // Checkbox Filter Component
  const CheckboxFilter = ({ label, checked, onChange, count }) => (
    <label className="checkbox-filter">
      <div className={`checkbox-box ${checked ? 'checked' : ''}`}>
        {checked && <Check size={14} />}
      </div>
      <span className="checkbox-label">{label}</span>
      {count !== undefined && <span className="checkbox-count">{count}</span>}
    </label>
  );

  // Toggle Filter Component
  const ToggleFilter = ({ label, icon: Icon, checked, onChange }) => (
    <div 
      className={`toggle-filter ${checked ? 'active' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <Icon size={16} />
      <span>{label}</span>
    </div>
  );

  return (
    <div className="search-page">
      <div className="search-container">
        {/* Mobile Filter Button */}
        <button 
          className="mobile-filter-btn"
          onClick={() => setMobileFiltersOpen(true)}
        >
          <SlidersHorizontal size={18} />
          Filtros
          {getActiveFilterCount() > 0 && (
            <span className="filter-badge">{getActiveFilterCount()}</span>
          )}
        </button>

        {/* Mobile Filter Overlay */}
        {mobileFiltersOpen && (
          <div className="mobile-filter-overlay" onClick={() => setMobileFiltersOpen(false)}>
            <div className="mobile-filter-panel" onClick={e => e.stopPropagation()}>
              <div className="mobile-filter-header">
                <h3>Filtros</h3>
                <button onClick={() => setMobileFiltersOpen(false)}>
                  <X size={24} />
                </button>
              </div>
              <div className="mobile-filter-content">
                {/* Same filter content as sidebar */}
                <FilterSection 
                  title="Categorías" 
                  icon={Grid3X3}
                  expanded={expandedSections.categories}
                  onToggle={() => toggleSection('categories')}
                >
                  <div className="category-list">
                    <CheckboxFilter
                      label="Todos"
                      checked={activeFilters.categories.length === 0}
                      onChange={() => handleCategorySelect(null)}
                      count={products.length}
                    />
                    {categories.map((category) => {
                      const count = products.filter(p => p.category?.id === category.id).length;
                      if (count === 0) return null;
                      return (
                        <CheckboxFilter
                          key={category.id}
                          label={category.title_translated || category.title}
                          checked={activeFilters.categories.includes(category.title)}
                          onChange={() => handleFilterChange('categories', category.title)}
                          count={count}
                        />
                      );
                    })}
                  </div>
                </FilterSection>
              </div>
              <div className="mobile-filter-footer">
                <button className="clear-btn" onClick={clearAllFilters}>
                  Limpiar Todo
                </button>
                <button className="apply-btn" onClick={() => setMobileFiltersOpen(false)}>
                  Aplicar Filtros
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="search-layout">
          {/* Sidebar */}
          <aside className="search-sidebar">
            <div className="sidebar-card">
              <div className="sidebar-header">
                <Filter size={20} />
                <h2>Filtros</h2>
                {getActiveFilterCount() > 0 && (
                  <button className="clear-all-btn" onClick={clearAllFilters}>
                    Limpiar
                  </button>
                )}
              </div>

              {/* Quick Availability Toggles */}
              <div className="availability-toggles">
                <ToggleFilter
                  label="En Stock"
                  icon={Check}
                  checked={activeFilters.inStock}
                  onChange={(v) => handleToggleFilter('inStock', v)}
                />
                <ToggleFilter
                  label="Envío Gratis"
                  icon={Truck}
                  checked={activeFilters.freeShipping}
                  onChange={(v) => handleToggleFilter('freeShipping', v)}
                />
                <ToggleFilter
                  label="En Oferta"
                  icon={Tag}
                  checked={activeFilters.onSale}
                  onChange={(v) => handleToggleFilter('onSale', v)}
                />
              </div>

              {/* Categories */}
              <FilterSection 
                title="Categorías" 
                icon={Grid3X3}
                expanded={expandedSections.categories}
                onToggle={() => toggleSection('categories')}
              >
                <div className="category-list">
                  <CheckboxFilter
                    label="Todas las Categorías"
                    checked={activeFilters.categories.length === 0}
                    onChange={() => handleCategorySelect(null)}
                    count={products.length}
                  />
                  {categories.map((category) => {
                    const count = products.filter(p => p.category?.id === category.id).length;
                    if (count === 0) return null;
                    return (
                      <CheckboxFilter
                        key={category.id}
                        label={category.title_translated || category.title}
                        checked={activeFilters.categories.includes(category.title)}
                        onChange={() => handleFilterChange('categories', category.title)}
                        count={count}
                      />
                    );
                  })}
                </div>
              </FilterSection>

              {/* Price Range */}
              <FilterSection 
                title="Precio" 
                icon={Tag}
                expanded={expandedSections.price}
                onToggle={() => toggleSection('price')}
              >
                <div className="price-range-section">
                  <div className="price-inputs">
                    <div className="price-input-group">
                      <span className="price-currency">$</span>
                      <input
                        type="number"
                        placeholder="0"
                        value={activeFilters.priceRange.min || ''}
                        onChange={(e) => handlePriceRangeChange(parseInt(e.target.value) || 0, activeFilters.priceRange.max)}
                      />
                    </div>
                    <span className="price-separator">—</span>
                    <div className="price-input-group">
                      <span className="price-currency">$</span>
                      <input
                        type="number"
                        placeholder="10000"
                        value={activeFilters.priceRange.max === 10000 ? '' : activeFilters.priceRange.max}
                        onChange={(e) => handlePriceRangeChange(activeFilters.priceRange.min, parseInt(e.target.value) || 10000)}
                      />
                    </div>
                  </div>
                  <div className="price-presets">
                    <button onClick={() => handlePriceRangeChange(0, 50)}>$0-50</button>
                    <button onClick={() => handlePriceRangeChange(50, 100)}>$50-100</button>
                    <button onClick={() => handlePriceRangeChange(100, 500)}>$100-500</button>
                    <button onClick={() => handlePriceRangeChange(500, 10000)}>$500+</button>
                  </div>
                </div>
              </FilterSection>

              {/* Vendors */}
              {uniqueVendors.length > 0 && (
                <FilterSection 
                  title="Vendedores" 
                  icon={Package}
                  expanded={expandedSections.vendors}
                  onToggle={() => toggleSection('vendors')}
                >
                  <div className="vendor-list">
                    {uniqueVendors.map((vendor) => {
                      const count = products.filter(p => p.vendor?.name === vendor).length;
                      return (
                        <CheckboxFilter
                          key={vendor}
                          label={vendor}
                          checked={activeFilters.vendors.includes(vendor)}
                          onChange={() => handleFilterChange('vendors', vendor)}
                          count={count}
                        />
                      );
                    })}
                  </div>
                </FilterSection>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <main className="search-main">
            {/* Results Bar */}
            <div className="results-bar">
              <div className="results-info">
                <span className="results-count">
                  <strong>{filteredProducts.length}</strong> productos
                </span>
                {getActiveFilterCount() > 0 && (
                  <div className="active-filters-tags">
                    {activeFilters.categories.map(cat => (
                      <span key={cat} className="filter-tag">
                        {cat}
                        <X size={14} onClick={() => handleFilterChange('categories', cat)} />
                      </span>
                    ))}
                    {activeFilters.inStock && (
                      <span className="filter-tag">
                        En Stock
                        <X size={14} onClick={() => handleToggleFilter('inStock', false)} />
                      </span>
                    )}
                    {activeFilters.freeShipping && (
                      <span className="filter-tag">
                        Envío Gratis
                        <X size={14} onClick={() => handleToggleFilter('freeShipping', false)} />
                      </span>
                    )}
                    {activeFilters.onSale && (
                      <span className="filter-tag">
                        En Oferta
                        <X size={14} onClick={() => handleToggleFilter('onSale', false)} />
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="results-controls">
                <div className="view-toggle">
                  <button 
                    className={viewMode === 'grid' ? 'active' : ''}
                    onClick={() => setViewMode('grid')}
                  >
                    <LayoutGrid size={18} />
                  </button>
                  <button 
                    className={viewMode === 'list' ? 'active' : ''}
                    onClick={() => setViewMode('list')}
                  >
                    <Grid3X3 size={18} />
                  </button>
                </div>
                <select 
                  className="sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="relevant">Más Relevante</option>
                  <option value="price-low">Precio: Menor a Mayor</option>
                  <option value="price-high">Precio: Mayor a Menor</option>
                  <option value="newest">Más Recientes</option>
                  <option value="discount">Mayor Descuento</option>
                </select>
              </div>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="products-grid">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="product-skeleton">
                    <div className="skeleton-image" />
                    <div className="skeleton-content">
                      <div className="skeleton-line" />
                      <div className="skeleton-line short" />
                      <div className="skeleton-line price" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <Package size={64} />
                </div>
                <h3>No se encontraron productos</h3>
                <p>
                  {query 
                    ? `No hay resultados para "${query}". Intenta con otros términos.`
                    : 'Ajusta los filtros para encontrar lo que buscas.'
                  }
                </p>
                <button className="clear-filters-btn" onClick={clearAllFilters}>
                  <X size={18} />
                  Limpiar Filtros
                </button>
              </div>
            ) : (
              <div className={`products-grid ${viewMode}`}>
                {filteredProducts.map((product) => {
                  const discount = product.old_price && product.old_price > product.price
                    ? Math.round(((product.old_price - product.price) / product.old_price) * 100)
                    : 0;
                  const isHovered = hoveredProduct === product.id;

                  return (
                    <div 
                      key={product.id}
                      className={`product-card ${isHovered ? 'hovered' : ''}`}
                      onMouseEnter={() => setHoveredProduct(product.id)}
                      onMouseLeave={() => {
                        setHoveredProduct(null);
                        handleImageMouseLeave();
                      }}
                    >
                      <Link to={`/detail/${product.slug}/`} className="product-link">
                        <div 
                          className="product-image-wrapper"
                          onMouseMove={handleImageMouseMove}
                        >
                          <div className="product-image-bg" />
                          <img
                            src={product.image}
                            alt={product.title_translated || product.title}
                            className="product-image"
                            style={{
                              transformOrigin: isHovered ? `${zoomOrigin.x}% ${zoomOrigin.y}%` : 'center center'
                            }}
                          />
                          
                          {/* Badges */}
                          <div className="product-badges">
                            {discount > 0 && (
                              <span className="badge-discount">
                                <Sparkles size={12} />
                                -{discount}%
                              </span>
                            )}
                            {product.shipping_amount === 0 && (
                              <span className="badge-shipping">
                                <Truck size={12} />
                                Gratis
                              </span>
                            )}
                          </div>

                          {/* Quick Actions */}
                          <div className={`quick-actions ${isHovered ? 'visible' : ''}`}>
                            <button 
                              className="action-btn wishlist"
                              onClick={(e) => addToWishlist(e, product)}
                            >
                              <Heart size={18} />
                            </button>
                            <button className="action-btn view">
                              <Eye size={18} />
                            </button>
                          </div>
                        </div>

                        <div className="product-content">
                          {product.category && (
                            <span className="product-category">
                              {product.category.title_translated || product.category.title}
                            </span>
                          )}
                          
                          <h3 className="product-title">
                            {product.title_translated || product.title}
                          </h3>

                          <div className="product-rating">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                size={14} 
                                fill={i < 4 ? '#fbbf24' : 'none'}
                                color={i < 4 ? '#fbbf24' : '#d1d5db'}
                              />
                            ))}
                            <span>4.5</span>
                          </div>

                          <div className="product-price-row">
                            <span className="product-price">
                              <PriceFormat_Basic value={product.price} />
                            </span>
                            {product.old_price > product.price && (
                              <span className="product-old-price">
                                <PriceFormat_Basic value={product.old_price} />
                              </span>
                            )}
                          </div>
                        </div>
                      </Link>

                      <button
                        className="add-to-cart-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          addToCart(product.id, product.price, product.shipping_ammount);
                        }}
                      >
                        <ShoppingCart size={18} />
                        Agregar
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </main>
        </div>
      </div>

      <style>{`
        .search-page {
          background: #ffffff;
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        /* Main Container */
        .search-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 24px 24px 60px;
        }

        /* Mobile Filter Button */
        .mobile-filter-btn {
          display: none;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 14px 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 600;
          color: #0f172a;
          cursor: pointer;
          margin-bottom: 16px;
        }

        .filter-badge {
          background: #3b82f6;
          color: white;
          font-size: 12px;
          padding: 2px 8px;
          border-radius: 100px;
          margin-left: auto;
        }

        /* Mobile Filter Overlay */
        .mobile-filter-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1000;
        }

        .mobile-filter-panel {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          max-height: 85vh;
          background: white;
          border-radius: 24px 24px 0 0;
          display: flex;
          flex-direction: column;
        }

        .mobile-filter-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e2e8f0;
        }

        .mobile-filter-header h3 {
          font-size: 18px;
          font-weight: 700;
          margin: 0;
        }

        .mobile-filter-header button {
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
        }

        .mobile-filter-content {
          flex: 1;
          overflow-y: auto;
          padding: 16px 24px;
        }

        .mobile-filter-footer {
          display: flex;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #e2e8f0;
        }

        .mobile-filter-footer button {
          flex: 1;
          padding: 14px;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
        }

        .clear-btn {
          background: #f1f5f9;
          border: none;
          color: #64748b;
        }

        .apply-btn {
          background: #0f172a;
          border: none;
          color: white;
        }

        /* Layout */
        .search-layout {
          display: flex;
          gap: 32px;
        }

        /* Sidebar */
        .search-sidebar {
          width: 300px;
          flex-shrink: 0;
        }

        .sidebar-card {
          background: white;
          border-radius: 20px;
          border: 1px solid #e2e8f0;
          padding: 24px;
          position: sticky;
          top: 24px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
        }

        .sidebar-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 2px solid #f1f5f9;
        }

        .sidebar-header h2 {
          font-size: 18px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .clear-all-btn {
          margin-left: auto;
          background: none;
          border: none;
          color: #3b82f6;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
        }

        /* Availability Toggles */
        .availability-toggles {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 20px;
        }

        .toggle-filter {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 10px 14px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 500;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .toggle-filter:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
        }

        .toggle-filter.active {
          background: #eff6ff;
          border-color: #3b82f6;
          color: #3b82f6;
        }

        /* Filter Section */
        .filter-section {
          margin-bottom: 16px;
        }

        .filter-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 0;
          cursor: pointer;
          border-bottom: 1px solid #f1f5f9;
        }

        .filter-section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 15px;
          font-weight: 600;
          color: #0f172a;
        }

        .filter-section-content {
          padding: 16px 0;
        }

        /* Checkbox Filter */
        .checkbox-filter {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 0;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .checkbox-filter:hover {
          background: #f8fafc;
          margin: 0 -8px;
          padding: 10px 8px;
          border-radius: 8px;
        }

        .checkbox-box {
          width: 20px;
          height: 20px;
          border: 2px solid #d1d5db;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .checkbox-box.checked {
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
        }

        .checkbox-label {
          flex: 1;
          font-size: 14px;
          color: #374151;
        }

        .checkbox-count {
          font-size: 12px;
          color: #94a3b8;
          background: #f1f5f9;
          padding: 2px 8px;
          border-radius: 100px;
        }

        /* Category List */
        .category-list,
        .vendor-list {
          max-height: 250px;
          overflow-y: auto;
        }

        /* Price Range */
        .price-range-section {
          padding: 8px 0;
        }

        .price-inputs {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        .price-input-group {
          flex: 1;
          display: flex;
          align-items: center;
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          overflow: hidden;
          transition: border-color 0.2s ease;
        }

        .price-input-group:focus-within {
          border-color: #3b82f6;
        }

        .price-currency {
          padding: 0 12px;
          color: #64748b;
          font-weight: 500;
        }

        .price-input-group input {
          flex: 1;
          padding: 12px 12px 12px 0;
          border: none;
          background: transparent;
          font-size: 14px;
          font-weight: 500;
          color: #0f172a;
          width: 60px;
        }

        .price-input-group input:focus {
          outline: none;
        }

        .price-separator {
          color: #94a3b8;
          font-weight: 600;
        }

        .price-presets {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .price-presets button {
          padding: 8px 12px;
          background: #f1f5f9;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .price-presets button:hover {
          background: #e2e8f0;
          color: #0f172a;
        }

        /* Main Content */
        .search-main {
          flex: 1;
          min-width: 0;
        }

        /* Results Bar */
        .results-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: white;
          padding: 16px 20px;
          border-radius: 14px;
          margin-bottom: 20px;
          border: 1px solid #e2e8f0;
          flex-wrap: wrap;
          gap: 12px;
        }

        .results-info {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .results-count {
          font-size: 14px;
          color: #64748b;
        }

        .results-count strong {
          color: #0f172a;
        }

        .active-filters-tags {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .filter-tag {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: #eff6ff;
          border-radius: 100px;
          font-size: 13px;
          font-weight: 500;
          color: #3b82f6;
        }

        .filter-tag svg {
          cursor: pointer;
        }

        .filter-tag svg:hover {
          color: #1d4ed8;
        }

        .results-controls {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .view-toggle {
          display: flex;
          background: #f1f5f9;
          border-radius: 10px;
          padding: 4px;
        }

        .view-toggle button {
          padding: 8px 12px;
          background: transparent;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          color: #64748b;
          transition: all 0.2s ease;
        }

        .view-toggle button.active {
          background: white;
          color: #0f172a;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .sort-select {
          padding: 10px 16px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 500;
          color: #0f172a;
          cursor: pointer;
        }

        /* Products Grid */
        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 24px;
        }

        .products-grid.list {
          grid-template-columns: 1fr;
        }

        /* Product Card */
        .product-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          border: 1px solid #f1f5f9;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          flex-direction: column;
        }

        .product-card.hovered {
          transform: translateY(-8px);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
          border-color: #e2e8f0;
        }

        .product-link {
          text-decoration: none;
          color: inherit;
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .product-image-wrapper {
          position: relative;
          height: 240px;
          overflow: hidden;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        }

        .product-image-bg {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 30% 30%, rgba(59, 130, 246, 0.06) 0%, transparent 50%);
        }

        .product-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 20px;
          transition: transform 0.2s ease-out;
        }

        .product-card.hovered .product-image {
          transform: scale(1.25);
        }

        .product-badges {
          position: absolute;
          top: 16px;
          left: 16px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .badge-discount {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          padding: 8px 12px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 700;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.35);
        }

        .badge-shipping {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          padding: 6px 10px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 600;
        }

        .quick-actions {
          position: absolute;
          top: 16px;
          right: 16px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          opacity: 0;
          transform: translateX(10px);
          transition: all 0.3s ease;
        }

        .quick-actions.visible {
          opacity: 1;
          transform: translateX(0);
        }

        .action-btn {
          width: 42px;
          height: 42px;
          border-radius: 12px;
          border: none;
          background: white;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          transition: all 0.2s ease;
        }

        .action-btn:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        }

        .action-btn.wishlist:hover {
          color: #ef4444;
          background: #fef2f2;
        }

        .action-btn.view:hover {
          color: #3b82f6;
          background: #eff6ff;
        }

        .product-content {
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          flex: 1;
        }

        .product-category {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #3b82f6;
          background: #eff6ff;
          padding: 5px 10px;
          border-radius: 6px;
          width: fit-content;
        }

        .product-title {
          font-size: 16px;
          font-weight: 600;
          color: #0f172a;
          line-height: 1.4;
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 45px;
        }

        .product-rating {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .product-rating span {
          font-size: 13px;
          color: #64748b;
          font-weight: 500;
          margin-left: 4px;
        }

        .product-price-row {
          display: flex;
          align-items: baseline;
          gap: 10px;
          margin-top: auto;
          padding-top: 12px;
        }

        .product-price {
          font-size: 22px;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: -0.5px;
        }

        .product-old-price {
          font-size: 15px;
          color: #94a3b8;
          text-decoration: line-through;
        }

        .add-to-cart-btn {
          margin: 0 20px 20px;
          padding: 14px 20px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.3s ease;
        }

        .add-to-cart-btn:hover {
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(15, 23, 42, 0.2);
        }

        /* Skeleton Loading */
        .product-skeleton {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          border: 1px solid #f1f5f9;
        }

        .skeleton-image {
          height: 240px;
          background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }

        .skeleton-content {
          padding: 20px;
        }

        .skeleton-line {
          height: 16px;
          background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 8px;
          margin-bottom: 12px;
        }

        .skeleton-line.short {
          width: 60%;
        }

        .skeleton-line.price {
          width: 40%;
          height: 24px;
        }

        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* Empty State */
        .empty-state {
          background: white;
          border-radius: 20px;
          padding: 80px 40px;
          text-align: center;
          border: 1px solid #e2e8f0;
        }

        .empty-icon {
          width: 100px;
          height: 100px;
          background: #f8fafc;
          border-radius: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
          color: #94a3b8;
        }

        .empty-state h3 {
          font-size: 22px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 12px;
        }

        .empty-state p {
          font-size: 15px;
          color: #64748b;
          margin: 0 0 28px;
          max-width: 400px;
          margin-left: auto;
          margin-right: auto;
        }

        .clear-filters-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: #0f172a;
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .clear-filters-btn:hover {
          background: #1e293b;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .search-sidebar {
            display: none;
          }

          .mobile-filter-btn {
            display: flex;
          }

          .mobile-filter-overlay {
            display: block;
          }

          .products-grid {
            grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
            gap: 16px;
          }
        }

        @media (max-width: 768px) {
          .search-container {
            padding: 16px;
          }

          .results-bar {
            padding: 12px 16px;
          }

          .view-toggle {
            display: none;
          }

          .products-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }

          .product-image-wrapper {
            height: 160px;
          }

          .product-content {
            padding: 14px;
          }

          .product-title {
            font-size: 13px;
            min-height: 36px;
          }

          .product-price {
            font-size: 17px;
          }

          .product-rating {
            display: none;
          }

          .add-to-cart-btn {
            margin: 0 14px 14px;
            padding: 12px;
            font-size: 13px;
          }
        }

        @media (max-width: 480px) {
          .products-grid {
            gap: 10px;
          }

          .product-image-wrapper {
            height: 140px;
          }

          .product-content {
            padding: 12px;
            gap: 6px;
          }

          .product-category {
            font-size: 10px;
            padding: 4px 8px;
          }

          .product-title {
            font-size: 12px;
            min-height: 32px;
          }

          .product-price {
            font-size: 15px;
          }

          .product-old-price {
            font-size: 12px;
          }

          .add-to-cart-btn {
            margin: 0 12px 12px;
            padding: 10px;
            font-size: 12px;
          }

          .badge-discount {
            padding: 6px 10px;
            font-size: 11px;
          }

          .quick-actions {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}

export default Search;

